import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { MdToday } from 'react-icons/md';

// types
import { InventoryItem, CALENDAR_VIEW } from 'types';
import { EventDetails } from 'queries/createEvent/types';
//assets
import { IoArrowBack } from 'react-icons/io5';
// components
import { DatePickers } from './DatePickers';
import { TimePicker } from './TimePicker';
import { Spinner } from 'style/Spinner';
// queries
import { useCreateUserCalendarEventMutation } from 'queries/createEvent/createUserCalendarEventMutation';
import { Button } from 'react-covideo-common';

const Row = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  align-items: flex-start;
  @media screen and (max-width: 520px) {
    margin-top: -20px;
    display: block;
  }
`;

type ColProps = {
  paddingOnMobile?: string;
};

const Col = styled.div<ColProps>`
  box-sizing: border-box;
  background: ${props => props.theme.colors.white[100]};
  border: 1px solid #eeeff2;
  border-radius: 7px;
  padding: 24px;
  width: 384px;
  max-width: 100%;
  @media screen and (max-width: 520px) {
    border: 0;
    border-radius: 0;
    width: 100%;
    padding: ${props => props.paddingOnMobile};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const GoBack = styled.div`
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  color: ${props => props.theme.colors.neutral[100]};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  margin-bottom: 32px;
  > span {
    margin-left: 8px;
  }
  @media screen and (max-width: 520px) {
    margin-top: 24px;
    margin-left: 20px;
  }
`;

type LabelProps = {
  marginOnMobile?: string;
};

const Label = styled.div<LabelProps>`
  color: ${props => props.theme.colors.neutral[80]};
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  @media screen and (max-width: 520px) {
    margin: ${props => props.marginOnMobile};
  }
`;

const Error = styled.div`
  color: ${props => props.theme.colors.danger[80]};
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
`;

type Props = {
  orderDetails: any;
  inventoryItem?: InventoryItem;
  setOrderDetails: (orderDetails: any) => void;
  onChangeView: (view: CALENDAR_VIEW) => void;
  mainColor?: string;
  mainTextColor?: string;
};
export const SchedulingDrive = ({
  orderDetails,
  onChangeView,
  setOrderDetails,
  inventoryItem,
  mainColor,
  mainTextColor,
}: Props) => {
  const today = new Date();
  const themes = useTheme();
  const tomorrow = new Date(today);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [error, setError] = useState('Choose date first');
  const [date, setDate] = useState<Date>(tomorrow);
  const [time, setTime] = useState<number>();
  const [eventDetails, setEventDetails] = useState<EventDetails>();
  const {
    mutate: createUserCalendarEventMutation,
    isLoading: isLoadingUserEvent,
    isSuccess,
  } = useCreateUserCalendarEventMutation();
  const onDateChange = (date: Date) => {
    if (date) setError('');
    setDate(date < tomorrow ? tomorrow : date);
  };
  const createDateTime = (h: number, d = new Date()) => {
    const newDateTime = new Date(d);
    newDateTime.setHours(0, 0, 0);
    newDateTime.setTime(newDateTime.getTime() + h * 60 * 60 * 1000);
    return newDateTime;
  };
  useEffect(() => {
    const dateTimeStart = createDateTime(time || 0, date);
    const dateTimeEnd = createDateTime(time ? time + 1 : 0, date);

    const names =
      orderDetails.firstName && orderDetails.lastName
        ? ` for ${orderDetails.firstName} ${orderDetails.lastName}`
        : ``;
    const summary = `Scheduled Test Drive${names}`;
    const description = `${inventoryItem?.make} ${inventoryItem?.model} ${inventoryItem?.stockNumber}`;
    setEventDetails({
      summary,
      description,
      start: {
        dateTime: dateTimeStart,
        timezone,
      },
      end: {
        dateTime: dateTimeEnd,
        timezone,
      },
      attendees: orderDetails?.email ? [orderDetails.email] : [],
    });
  }, [date, time, timezone, orderDetails]);

  useEffect(() => {
    if (isSuccess) {
      if (!eventDetails) return;
      setOrderDetails({
        ...orderDetails,
        scheduledDatetime: eventDetails?.start?.dateTime,
      });
      onChangeView(CALENDAR_VIEW.SCHEDULED);
      return;
    }
  }, [isSuccess]);

  const createEvent = () => {
    if (!eventDetails) return;
    createUserCalendarEventMutation({ data: eventDetails });
  };

  return (
    <Row>
      <Col paddingOnMobile='24px 0'>
        <GoBack onClick={() => onChangeView(CALENDAR_VIEW.LANDING)}>
          <IoArrowBack size={18} />
          <span>Back to Offer</span>
        </GoBack>
        <Label marginOnMobile='0 0 0 20px'>Choose Date</Label>
        <DatePickers onDateChange={onDateChange} date={date} />
      </Col>
      <Col paddingOnMobile='24px 20px'>
        <Header>
          <Label>Choose Time</Label>
          <Error>{error}</Error>
        </Header>
        <TimePicker disabled={!!error} time={time} onChangeTime={setTime} />
        <Button
          disabled={!date || !time || isLoadingUserEvent}
          text={isLoadingUserEvent ? 'Scheduling...' : 'Schedule Test Drive'}
          icon={
            isLoadingUserEvent ? (
              <Spinner />
            ) : (
              <MdToday color={themes.colors.white[100]} />
            )
          }
          style={{
            backgroundColor: mainColor,
            color: mainTextColor,
            borderColor: mainColor,
            width: '100%',
          }}
          onClick={() => createEvent()}
        />
      </Col>
    </Row>
  );
};
